import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getSuppressionActive,
  hasShownThisSession,
  markShownThisSession,
} from './sponsorPopupStorage';

/**
 * How long a visitor must spend with the tab visible, in this browser
 * session, before the sponsor popup is eligible to appear. Exported so
 * scripts/verify-performance.js can assert the delay directly from source
 * rather than trusting a comment (runtime:sponsor-popup-delay-at-least-60s).
 */
export const SPONSOR_POPUP_DELAY_MS = 60_000;

// Organiser, admin, account, auth, legal and partners routes must never be
// interrupted by the popup. Everything else (home, /events/*, /sets,
// /sets/*, /saved-events, unknown routes) is allowed.
const DENIED_ROUTE_PREFIXES = [
  '/submit-event',
  '/my-events',
  '/admin',
  '/settings',
  '/verify',
  '/reset-password',
  '/privacy',
  '/contact',
  '/partners',
  '/release-notes',
];

// While the timer is waiting for a clear moment to open, a cheap sweep
// catches cases a route change or focusout would miss (a visitor who leaves
// a search field focused for a while). This is deliberately not the main
// clock above: it only runs once the 60 second timer has already elapsed,
// and reschedules itself one setTimeout at a time rather than ticking.
const DEFERRED_RECHECK_MS = 5_000;

export const isDeniedRoute = (pathname: string): boolean => (
  DENIED_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
);

const isBlockingOverlayOpen = (): boolean => {
  if (typeof document === 'undefined') return false;
  // NavBar's mobile drawer and Calendar's mobile month drawer both lock body
  // scroll this way; the month drawer and every modal in the app (AuthModal,
  // EventModal, PromotionRequestModal) also render role="dialog"
  // aria-modal="true". UpdateNotification is a non-modal toast with neither,
  // so it opts in with data-overlay-open instead (see UpdateNotification.tsx).
  if (document.body.style.overflow === 'hidden') return true;
  if (document.querySelector('[role="dialog"][aria-modal="true"]')) return true;
  if (document.querySelector('[data-overlay-open="true"]')) return true;
  return false;
};

// Shared by isTypingInField (checks the currently focused element) and the
// focusout handler below (checks the element ABOUT to receive focus, via
// event.relatedTarget): a visitor tabbing from one field to another must
// stay deferred throughout, not just while document.activeElement briefly
// reads as document.body between the two focus events.
const isTypingField = (element: Element | null): boolean => {
  if (!(element instanceof HTMLElement)) return false;
  if (element.isContentEditable) return true;
  return element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.tagName === 'SELECT';
};

const isTypingInField = (): boolean => (
  isTypingField(typeof document !== 'undefined' ? document.activeElement : null)
);

/**
 * Arms the sponsor popup after SPONSOR_POPUP_DELAY_MS of session-visible
 * browsing time (time with the tab hidden does not count; the clock pauses
 * and resumes on visibilitychange), then waits for a moment that will not
 * interrupt the visitor: an allowed route, no other dialog/drawer open, and
 * no text field focused. Calls onReady at most once per session.
 *
 * Mount this once, as a sibling of <Routes> inside the Router (never inside
 * a route element), so the clock survives SPA navigation instead of
 * resetting on every route change.
 */
export const useSponsorPopupSchedule = (onReady: () => void): void => {
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  // Populated by the timer-owning effect below once it has mounted; calling
  // it asks for an immediate re-check of the open conditions. Exposed so the
  // route-change effect (a "cheap trigger" in its own right) can ask for one
  // without owning any of the timer state itself.
  const requestRecheckRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
    // Already shown this session, or still inside a dismissal window: skip
    // arming anything at all rather than running a 60 second timer and a
    // permanent capturing focusout listener for a visitor who is never going
    // to see the popup.
    if (hasShownThisSession() || getSuppressionActive()) return undefined;

    let cancelled = false;
    let fired = false;
    let elapsedComplete = false;
    let visibleTimer: ReturnType<typeof setTimeout> | null = null;
    let recheckTimer: ReturnType<typeof setTimeout> | null = null;
    let remainingMs = SPONSOR_POPUP_DELAY_MS;
    let visibleSince = document.visibilityState === 'hidden' ? null : Date.now();

    const clearVisibleTimer = () => {
      if (visibleTimer) {
        clearTimeout(visibleTimer);
        visibleTimer = null;
      }
    };

    const clearRecheckTimer = () => {
      if (recheckTimer) {
        clearTimeout(recheckTimer);
        recheckTimer = null;
      }
    };

    // Extracted so both a normal unmount and an in-flight "turns out this
    // session is done" discovery inside tryOpen tear down the same way:
    // clear both timers and drop both document listeners so nothing keeps
    // running for the rest of the session.
    const cleanupListeners = () => {
      clearVisibleTimer();
      clearRecheckTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('focusout', handleFocusOut, true);
    };

    const scheduleRecheck = () => {
      clearRecheckTimer();
      recheckTimer = setTimeout(() => {
        recheckTimer = null;
        tryOpen();
      }, DEFERRED_RECHECK_MS);
    };

    function tryOpen() {
      if (cancelled || fired || !elapsedComplete) return;
      if (hasShownThisSession() || getSuppressionActive()) {
        // Became true after this hook mounted (another tab showed it, or
        // suppressed it, while this one was still deferred). Stop for the
        // rest of the session instead of continuing to poll.
        fired = true;
        cleanupListeners();
        return;
      }

      if (
        isDeniedRoute(pathnameRef.current)
        || isBlockingOverlayOpen()
        || isTypingInField()
      ) {
        scheduleRecheck();
        return;
      }

      fired = true;
      cleanupListeners();
      markShownThisSession();
      onReadyRef.current();
    }

    const armVisibleTimer = () => {
      clearVisibleTimer();
      if (remainingMs <= 0) {
        elapsedComplete = true;
        tryOpen();
        return;
      }
      visibleTimer = setTimeout(() => {
        visibleTimer = null;
        elapsedComplete = true;
        tryOpen();
      }, remainingMs);
    };

    if (document.visibilityState !== 'hidden') {
      armVisibleTimer();
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        if (visibleSince !== null) {
          remainingMs -= Date.now() - visibleSince;
          visibleSince = null;
        }
        clearVisibleTimer();
        return;
      }

      if (!elapsedComplete && visibleSince === null) {
        visibleSince = Date.now();
        armVisibleTimer();
        return;
      }

      if (elapsedComplete) {
        // The delay had already elapsed before the tab was hidden (deferred
        // on a blocked route/overlay/field). Give a short grace period after
        // returning rather than popping the popup up the instant the tab
        // regains focus.
        scheduleRecheck();
      }
    }

    function handleFocusOut(event: FocusEvent) {
      if (!elapsedComplete) return;

      // event.relatedTarget is the element ABOUT to receive focus. Checking
      // it (rather than document.activeElement, which already reads as
      // document.body mid-transition) stops a visitor tabbing from one field
      // to another from being interrupted between the two focus events.
      if (isTypingField(event.relatedTarget as Element | null)) {
        scheduleRecheck();
        return;
      }

      tryOpen();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('focusout', handleFocusOut, true);
    requestRecheckRef.current = () => {
      if (elapsedComplete) tryOpen();
    };

    return () => {
      cancelled = true;
      cleanupListeners();
      requestRecheckRef.current = () => {};
    };
    // The mount effect above owns the visible-time clock end to end; it must
    // not be recreated on every route change (that would reset the delay).
    // Route changes are read fresh from pathnameRef, and the effect below
    // asks this effect's own handler for an immediate re-check instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previousPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (previousPathnameRef.current === location.pathname) return;
    previousPathnameRef.current = location.pathname;
    requestRecheckRef.current();
  }, [location.pathname]);
};
